import React, { useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  downloadCompatibilityReportPdf,
  extractSubmitErrorMessage,
} from '../../../api/compatibilityReportsApi';

interface GenerateStakeholderReportButtonProps {
  /** Finished job / history report id. */
  reportId: string;
  className?: string;
}

/**
 * Download PDF from random-compat API:
 * GET /v1/compatibility-reports/{id}/pdf → uses response.url for auto-download.
 */
export const GenerateStakeholderReportButton: React.FC<
  GenerateStakeholderReportButtonProps
> = ({ reportId, className }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDownload = Boolean(reportId) && reportId !== 'sync';

  const handleClick = async () => {
    if (!canDownload) {
      setError('PDF needs a real report/job id (not a sync-only response).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await downloadCompatibilityReportPdf(reportId);
    } catch (err) {
      setError(extractSubmitErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      <Button
        type="button"
        className="h-9 shrink-0 bg-hero-red hover:bg-hero-red/90 text-white"
        onClick={() => void handleClick()}
        disabled={busy || !canDownload}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileDown className="h-4 w-4" />
        )}
        <span className="ml-2">{busy ? 'Generating…' : 'Generate report'}</span>
      </Button>
      {error && (
        <p className="text-[12px] text-hero-red mt-1 max-w-[260px] sm:max-w-none">{error}</p>
      )}
    </div>
  );
};
